
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Users, DollarSign, FileText, Edit, Trash2, Eye } from 'lucide-react';
import { AssignProviderModal } from '@/components/Events/AssignProviderModal';
import { ProviderInfoModal } from '@/components/Events/ProviderInfoModal';
import { PaymentModal } from '@/components/Events/PaymentModal';
import { DocumentsModal } from '@/components/Events/DocumentsModal';
import { EditProviderModal } from '@/components/Events/EditProviderModal';
import { useEventPermission } from '@/hooks/useEventPermission';

interface Provider {
  id: string;
  provider: { 
    nombre: string; 
    tipo: string;
    telefono?: string;
    email?: string;
  };
  descripcion_servicio: string;
  precio: number;
  pagado: number;
  notas?: string;
}

export const EventProviders: React.FC = () => {
  const { eventId } = useParams();
  const { canWrite } = useEventPermission(eventId);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showProviderInfoModal, setShowProviderInfoModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [eventProviders, setEventProviders] = useState<Provider[]>([
    {
      id: '1',
      provider: { 
        nombre: 'Fotografía Profesional SA', 
        tipo: 'Fotografía',
        telefono: '+52 55 1234 5678',
        email: 'contacto@fotoprofesional.com'
      },
      descripcion_servicio: 'Fotografía y video completo del evento',
      precio: 25000,
      pagado: 15000,
      notas: 'Incluye álbum digital y 50 fotos impresas'
    },
    {
      id: '2',
      provider: { 
        nombre: 'Banquetes Los Pinos', 
        tipo: 'Catering',
        telefono: '+52 55 8765 4321',
        email: 'ventas@banqueteslospinos.com'
      },
      descripcion_servicio: 'Cena completa para 150 personas',
      precio: 75000,
      pagado: 75000
    },
    {
      id: '3',
      provider: { 
        nombre: 'Flores del Valle', 
        tipo: 'Decoración',
        telefono: '+52 55 9876 5432',
        email: 'info@floresdelvalle.com'
      },
      descripcion_servicio: 'Arreglos florales y decoración del lugar',
      precio: 18000,
      pagado: 5000,
      notas: 'Coordinación especial para montaje 2 horas antes'
    }
  ]);

  // Mock data - esto vendrá del servicio
  const event = {
    id: eventId,
    nombre: 'Boda de Ana y Carlos',
    fecha: '15/12/2024'
  };

  const getPaymentStatus = (precio: number, pagado: number) => {
    const percentage = (pagado / precio) * 100;
    if (percentage === 100) return <Badge className="bg-emerald-100 text-emerald-800">Pagado</Badge>;
    if (percentage > 0) return <Badge className="bg-amber-100 text-amber-800">Parcial</Badge>;
    return <Badge className="bg-red-100 text-red-800">Pendiente</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const handleProviderClick = (provider: Provider) => {
    setSelectedProvider(provider);
    setShowProviderInfoModal(true);
  };

  const handleSaveNotes = (providerId: string, notes: string) => {
    setEventProviders(prevProviders =>
      prevProviders.map(p => 
        p.id === providerId ? { ...p, notas: notes } : p
      )
    );
  };

  const handlePaymentRegistered = (providerId: string, newAmount: number, notes?: string) => {
    setEventProviders(prevProviders =>
      prevProviders.map(p => 
        p.id === providerId 
          ? { ...p, pagado: newAmount, notas: notes ? p.notas + '\n' + notes : p.notas } 
          : p
      )
    );
  };

  const handleProviderUpdated = (providerId: string, updatedData: any) => {
    setEventProviders(prevProviders =>
      prevProviders.map(p => 
        p.id === providerId ? { ...p, ...updatedData } : p
      )
    );
  };

  const handlePaymentClick = (provider: Provider) => {
    setSelectedProvider(provider);
    setShowPaymentModal(true);
  };

  const handleDocumentsClick = (provider: Provider) => {
    setSelectedProvider(provider);
    setShowDocumentsModal(true);
  };

  const handleEditClick = (provider: Provider) => {
    setSelectedProvider(provider);
    setShowEditModal(true);
  };

  const handleDeleteProvider = (providerId: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar este proveedor del evento?')) {
      setEventProviders(prevProviders =>
        prevProviders.filter(p => p.id !== providerId)
      );
    }
  };

  const totalBudget = eventProviders.reduce((sum, ep) => sum + ep.precio, 0);
  const totalPaid = eventProviders.reduce((sum, ep) => sum + ep.pagado, 0);
  const pendingPayment = totalBudget - totalPaid;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Proveedores del Evento</h1>
          <p className="text-gray-600">{event.nombre} - {event.fecha}</p>
        </div>
        <Button
          onClick={() => setShowAssignModal(true)}
          disabled={!canWrite}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Asignar Proveedor
        </Button>
      </div>

      {/* Estadísticas financieras */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{formatCurrency(totalBudget)}</div>
            <p className="text-gray-600">Presupuesto Total</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(totalPaid)}</div>
            <p className="text-gray-600">Total Pagado</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">{formatCurrency(pendingPayment)}</div>
            <p className="text-gray-600">Saldo Pendiente</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-purple-600">{eventProviders.length}</div>
            <p className="text-gray-600">Proveedores</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de proveedores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Proveedores Asignados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Servicio</TableHead>
                <TableHead>Precio</TableHead>
                <TableHead>Pagado</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Saldo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eventProviders.map((ep) => (
                <TableRow 
                  key={ep.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleProviderClick(ep)}
                >
                  <TableCell className="font-medium">{ep.provider.nombre}</TableCell>
                  <TableCell>{ep.provider.tipo}</TableCell>
                  <TableCell>{ep.descripcion_servicio}</TableCell>
                  <TableCell>{formatCurrency(ep.precio)}</TableCell>
                  <TableCell>{formatCurrency(ep.pagado)}</TableCell>
                  <TableCell>{getPaymentStatus(ep.precio, ep.pagado)}</TableCell>
                  <TableCell className="font-semibold">
                    {formatCurrency(ep.precio - ep.pagado)}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        title="Ver información"
                        onClick={() => handleProviderClick(ep)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        title="Registrar pago"
                        onClick={() => handlePaymentClick(ep)}
                      >
                        <DollarSign className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        title="Ver documentos"
                        onClick={() => handleDocumentsClick(ep)}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        title="Editar"
                        onClick={() => handleEditClick(ep)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        title="Eliminar"
                        onClick={() => handleDeleteProvider(ep.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AssignProviderModal 
        open={showAssignModal} 
        onOpenChange={setShowAssignModal}
      />

      <ProviderInfoModal
        open={showProviderInfoModal}
        onOpenChange={setShowProviderInfoModal}
        provider={selectedProvider}
        onSaveNotes={handleSaveNotes}
      />

      <PaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        provider={selectedProvider}
        onPaymentRegistered={handlePaymentRegistered}
      />

      <DocumentsModal
        open={showDocumentsModal}
        onOpenChange={setShowDocumentsModal}
        provider={selectedProvider}
      />

      <EditProviderModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        provider={selectedProvider}
        onProviderUpdated={handleProviderUpdated}
      />
    </div>
  );
};
